import { createSlice, createSelector } from "@reduxjs/toolkit";
import { VIDEO_SESSION_PAGES as VSP } from "@app/common/data/ActivitiessEnums";
import { createSimpleSelector, LOCKED_TXT, RECORDING_TEXT, RECORDING_PAUSED_TEXT, RESOURCE_PERMISSION_DENIED, simpleSetPayload } from "../constants";
import { SLICE_NAMES } from "./sliceNames";

export const GAME_TIMER_SW_STATUS = {
  RUNNING: "running",
  PAUSED: "paused",
  STOPPED: "stopped",
};

const initialState = {
  isLocked: false,
  boardMode: VSP.SESSION_START,
  drawingPresent: false,
  gameTimerStatus: "",
  gameSWStatus: "",
  TherapistTime: { minutes: 0, seconds: 0 },
  studioActivity: null,
  previewStudioActivity: null,
  h5pGame: {},
  isH5PGameDone: false,
  h5pGamePostMessageHandler: () => {},
  previewH5PGame: {},
  isPreviewH5PGameDone: false,
  previewH5PGamePostMessageHandler: () => {},
  articulationWord: null,
  isRecording: false,
  isRecordingPaused: false,
  articulationResult: null,
  wordUUID: null,
  speechUUID: null,
  isTeacherPresenting: false,
  previewMode: null,
  recordingErr: null,
  isInterruptFromTherapist: false,
  isEnabledRecording: false,
  isKickedFromTreatment: false,
  selfPracticeGame: null,
  currentSelfPractice: null,
};

export const treatmentStudentSideSlice = createSlice({
  name: SLICE_NAMES.STUDENT_SIDE_SLICE,
  initialState,
  reducers: {
    clearStudent: () => ({ ...initialState }),
    setIsLocked: simpleSetPayload("isLocked"),
    setSessionStatus: simpleSetPayload("sessionStatus"),
    setBoardMode: simpleSetPayload("boardMode"),
    setDrawingPresent: simpleSetPayload("drawingPresent"),
    setGameTimerStatus: simpleSetPayload("gameTimerStatus"),
    setGameSWStatus: simpleSetPayload("gameSWStatus"),
    setTherapistTime: simpleSetPayload("TherapistTime"),
    setStudioActivity: simpleSetPayload("studioActivity"),
    setH5PGame: simpleSetPayload("h5pGame"),
    setIsH5PGameDone: simpleSetPayload("isH5PGameDone"),
    setH5PGamePostMessageHandler: simpleSetPayload("h5pGamePostMessageHandler"),
    setArticulationWord: simpleSetPayload("articulationWord"),
    setIsRecording: simpleSetPayload("isRecording"),
    setIsRecordingPaused: simpleSetPayload("isRecordingPaused"),
    setArticulationResult: simpleSetPayload("articulationResult"),
    setWordUUID: simpleSetPayload("wordUUID"),
    setSpeechUUID: simpleSetPayload("speechUUID"),
    setCurrentSpeechRecognition: () => {},
    sendRecordingsCount: () => {},
    setIsTeacherPresenting: simpleSetPayload("isTeacherPresenting"),
    setPreviewMode: simpleSetPayload("previewMode"),
    setPreviewStudioActivity: simpleSetPayload("previewStudioActivity"),
    setPreviewH5PGame: (state, action) => {
      state.previewH5PGame = action?.payload;
      state.isPreviewH5PGameDone = false;
    },
    setIsPreviewH5PGameDone: simpleSetPayload("isPreviewH5PGameDone"),
    setPreviewH5PGamePostMessageHandler: simpleSetPayload("previewH5PGamePostMessageHandler"),
    setRecordingErr: simpleSetPayload("recordingErr"),
    setIsInterruptFromTherapist: simpleSetPayload("isInterruptFromTherapist"),
    setIsKickedFromTreatment: simpleSetPayload("isKickedFromTreatment"),
    setIsEnabledRecording: (state, action) => {
      state.isEnabledRecording = action.payload;
      state.isRecordingPaused = false;
    },
    setSelfPracticeGame: simpleSetPayload("selfPracticeGame"),
    setCurrentSelfPractice: simpleSetPayload("currentSelfPractice"),
  },
  extraReducers: {
  },
});

export default treatmentStudentSideSlice.reducer;

//actions

export const {
  setIsLocked,
  setBoardMode,
  setDrawingPresent,
  setSessionStatus,
  setStudioActivity,
  setH5PGame,
  setCurrentSpeechRecognition,
  clearStudent,
  setIsTeacherPresenting,
  setPreviewMode,
  setPreviewStudioActivity,
  setIsRecording,
  setIsRecordingPaused,
  setGameSWStatus,
  setGameTimerStatus,
  setTherapistTime,
  setArticulationWord,
  setArticulationResult,
  setWordUUID,
  setSpeechUUID,
  setH5PGamePostMessageHandler,
  setPreviewH5PGame,
  setPreviewH5PGamePostMessageHandler,
  sendRecordingsCount,
  setRecordingErr,
  setIsInterruptFromTherapist,
  setIsEnabledRecording,
  setIsH5PGameDone,
  setIsPreviewH5PGameDone,
  setIsKickedFromTreatment,
  setSelfPracticeGame,
  setCurrentSelfPractice,
} = treatmentStudentSideSlice.actions;

//selectors
// const simpleFieldSelector = simpleFieldSelectorFunc(rootSelector);
const getIsLocked = state => state[SLICE_NAMES.STUDENT_SIDE_SLICE].isLocked;
const getRecordingErr = state => state[SLICE_NAMES.STUDENT_SIDE_SLICE].recordingErr;
const getIsRecording = state => state[SLICE_NAMES.STUDENT_SIDE_SLICE].isRecording;
const getIsPaused = state => state[SLICE_NAMES.STUDENT_SIDE_SLICE].isRecordingPaused;

const createStudentSliceSimpleSelector = field => createSimpleSelector("treatmentStudentSideSlice", field);

export const selectIsLocked = createStudentSliceSimpleSelector("isLocked");
export const selectSessionStatus = createStudentSliceSimpleSelector("sessionStatus");
export const selectBoardMode = createStudentSliceSimpleSelector("boardMode");
export const selectPresentingDrawing = createStudentSliceSimpleSelector("drawingPresent");
export const selectGameTimerStatus = createStudentSliceSimpleSelector("gameTimerStatus");
export const selectGameSWStatus = createStudentSliceSimpleSelector("gameSWStatus");
export const selectTherapistTime = createStudentSliceSimpleSelector("TherapistTime");
export const selectStudioActivity = createStudentSliceSimpleSelector("studioActivity");
export const selectH5pGame = createStudentSliceSimpleSelector("h5pGame");
export const selectIsH5PGameDone = createStudentSliceSimpleSelector("isH5PGameDone");
export const selectArticulationWord = createStudentSliceSimpleSelector("articulationWord");
export const selectIsRecording = createStudentSliceSimpleSelector("isRecording");
export const selectIsRecordingPaused = createStudentSliceSimpleSelector("isRecordingPaused");
export const selectArticulationResult = createStudentSliceSimpleSelector("articulationResult");
export const selectWordUUID = createStudentSliceSimpleSelector("wordUUID");
export const selectSpeechUUID = createStudentSliceSimpleSelector("speechUUID");
export const selectH5PGamePostMessageHandler = createStudentSliceSimpleSelector("h5pGamePostMessageHandler");
export const selectIsTeacherPresenting = createStudentSliceSimpleSelector("isTeacherPresenting");
export const selectPreviewMode = createStudentSliceSimpleSelector("previewMode");
export const selectPreviewStudioActivity = createStudentSliceSimpleSelector("previewStudioActivity");
export const selectPreviewH5PGame = createStudentSliceSimpleSelector("previewH5PGame");
export const selectIsPreviewH5PGameDone = createStudentSliceSimpleSelector("isPreviewH5PGameDone");
export const selectPreviewH5PGamePostMessageHandler = createStudentSliceSimpleSelector("previewH5PGamePostMessageHandler");
export const selectRecordingErr = createStudentSliceSimpleSelector("recordingErr");
export const selectIsInterruptFromTherapist = createStudentSliceSimpleSelector("isInterruptFromTherapist");
export const selectIsEnabledRecording = createStudentSliceSimpleSelector("isEnabledRecording");
export const selectIsKickedFromTreatment = createStudentSliceSimpleSelector("isKickedFromTreatment");
export const selectSelfPracticeGame = createStudentSliceSimpleSelector("selfPracticeGame");
export const selectCurrentSelfPractice = createStudentSliceSimpleSelector("currentSelfPractice");

export const selectIndicationText = createSelector(
  [getIsLocked, getRecordingErr, getIsRecording, getIsPaused], (isLocked, recordingErr, isRecording, isRecordingPaused) => {
    if (recordingErr) {
      return RESOURCE_PERMISSION_DENIED;
    }
    if (isRecordingPaused) {
      return RECORDING_PAUSED_TEXT;
    }
    if (isRecording) {
      return RECORDING_TEXT;
    }
    if (isLocked) {
      return LOCKED_TXT;
    }
    return "";
  },
);
